import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';

import { SortOrder } from '@cast/types';
import { EnterpriseAPIListMembersStatusEnum } from '@cast/types-organization-management';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

import { overrideOrganizationHeader } from '@cast/api-client';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

export const ENTERPRISE_USERS_QUERY_KEY = 'enterprise-users';

export const getEnterpriseMembersInfiniteQueryKey = (params: {
  enterpriseId: string | undefined;
  pageLimit?: string;
  organizationId?: string[];
  groupId?: string[];
  roleId?: string[];
  name?: string;
  status?: EnterpriseAPIListMembersStatusEnum;
  sortField?: string;
  sortOrder?: SortOrder;
}) =>
  [
    ENTERPRISE_USERS_QUERY_KEY,
    params.enterpriseId,
    params.pageLimit,
    params.organizationId,
    params.groupId,
    params.roleId,
    params.name,
    params.status,
    params.sortField,
    params.sortOrder,
  ] as const;

type EnterpriseMembersQueryParams = {
  enterpriseId: string;
  pageLimit?: string;
  organizationId?: string[];
  groupId?: string[];
  roleId?: string[];
  name?: string;
  status?: EnterpriseAPIListMembersStatusEnum;
  sortField?: string;
  sortOrder?: SortOrder;
};

const getEnterpriseMembersQueryOptions = ({
  enterpriseId,
  pageLimit = '50',
  organizationId,
  groupId,
  roleId,
  name,
  status,
  sortField,
  sortOrder,
  enabled = true,
}: EnterpriseMembersQueryParams & { enabled?: boolean }) =>
  infiniteQueryOptions({
    queryKey: getEnterpriseMembersInfiniteQueryKey({
      enterpriseId,
      pageLimit,
      organizationId,
      groupId,
      roleId,
      name,
      status,
      sortField,
      sortOrder,
    }),
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.enterprises.listMembers(
        {
          enterpriseId,
          pageLimit,
          groupId,
          organizationId,
          pageCursor: pageParam,
          roleId,
          name,
          status,
          sortField,
          sortOrder,
        },
        {
          headers: overrideOrganizationHeader(enterpriseId),
        }
      );
      return data;
    },
    refetchOnWindowFocus: false,
    initialPageParam: '0',
    getNextPageParam: ({ nextPageCursor }) =>
      nextPageCursor ? nextPageCursor : undefined,
    getPreviousPageParam: ({ prevPageCursor }) =>
      prevPageCursor ? prevPageCursor : undefined,
    maxPages: 1,
    enabled: makeIsEnabled(!!enterpriseId, enabled),
  });

type UseEnterpriseMembersInfiniteQueryArgs = {
  enabled?: boolean;
  pageLimit?: string;
  organizationId?: string[];
  groupId?: string[];
  roleId?: string[];
  freeText?: string;
  status?: EnterpriseAPIListMembersStatusEnum;
  sortField?: string;
  sortOrder?: SortOrder;
};

export const useEnterpriseMembersInfiniteQuery = (
  args?: UseEnterpriseMembersInfiniteQueryArgs
) => {
  const { enterpriseOrgId } = useOrganizations();

  const {
    enabled = true,
    pageLimit,
    groupId,
    organizationId,
    roleId,
    freeText: name,
    status,
    sortField,
    sortOrder,
  } = args || {};

  return useInfiniteQuery(
    getEnterpriseMembersQueryOptions({
      enterpriseId: enterpriseOrgId ?? '',
      pageLimit,
      groupId,
      organizationId,
      roleId,
      name,
      status,
      sortField,
      sortOrder,
      enabled,
    })
  );
};
